import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import addLogDocument from "../addLogDocument.js"; // Assuming a custom log function

// Function to create a new invoice/devis
const createNewDevis = async (devis, author, token, prefix) => {
  try {
    console.log("Creating new invoice with author:", author);
    const devisid = devis.id;
    const url = prefix + `devis/${devisid}/generate-invoice`;

    const masteradresse = devis.master_adresse;
    const masterpostal = devis.master_postal;
    const masterville = devis.master_ville;
    const mastername = devis.master_name;
    const marque = devis.marque || "";
    const siret = devis.siret;
    const numtva = devis.num_tva;
    const phone = devis.phone;
    const email = devis.email;
    const nomclient = devis.nom_client;
    const civiliteclient = devis.civilite_client || "";
    const adresseclient = devis.adresse_client;
    const postalclient = devis.postal_client;
    const villeclientt = devis.ville_client;
    const phoneclient = devis.tel_client;
    const emailclient = devis.email_client;
    const adresseprojet = devis.adresse_projet;
    const postalprojet = devis.postal_projet;
    const villeprojet = devis.ville_projet;
    const numdevis = devis.num_devis;
    const date = devis.date;
    const datevalidite = devis.date_validite;
    const banque = devis.banque;
    const iban = devis.iban;
    const bic = devis.bic;
    const conditionsreglement = devis.conditions_reglement || "";
    const politiqueannulation = devis.politique_annulation || "";
    const conditions_generales_vente = devis.conditions_generales_vente || "";
    const politiqueechec = devis.politique_echec || "";
    const articles = devis.articles || [];

    const logourl = devis.logo_url;

    let subtotal_ht = 0;
    let montant_tva = 0;
    let total_ttc = 0;
    const theproducts = [];
    for (let article of articles) {
      if (!article) continue;
      const pro = {
        name: article?.name || "Pas de nom",
        quantity: (article?.quantity || 1).toString(),
        discount: (article?.discount?.type === "percent"
          ? article?.discount.value + "%"
          : formatmoney(article?.discount?.value || 0)
        ).toString(),
        unit_price_ht: formatmoney(article.price),
        supplement: formatmoney(article.supplement || 0),
        price_ht_after_discount: formatmoney(article.total_ht_rem),
        conditions_reglement: article.conditions_reglement || "",
        price_ttc: formatmoney(article.total_ttc),
        description: article.description,
        pack_id: article.pack_ref?.id || "",
      };
      theproducts.push(pro);
      subtotal_ht += article.saved_np;
      montant_tva += article.saved_np * 0.2;
      total_ttc += article.saved_np * 1.2;
    }

    const base_ht_string = formatmoney(devis.base_ht);
    const supplement_string = formatmoney(devis.supplement);
    const total_remise_string = formatmoney(devis.total_remise);
    const total_ht_string = formatmoney(devis.total_ht);
    const total_ttc_string = formatmoney(devis.total_ttc);
    const tva_string = formatmoney(devis.tva);
    const nbr_maisons = devis.nombre_maisons?.toString();
    const surface = devis.surface?.toString();

    const data = {
      devis_id: devisid,
      author: author,
      master: {
        logo_url: logourl,
        name: mastername,
        address: masteradresse,
        postal_code: masterpostal,
        city: masterville,
        siret: siret,
        num_tva: numtva,
        phone: phone,
        email: email,
      },
      client: {
        name: nomclient,
        address: adresseclient,
        postal_code: postalclient,
        city: villeclientt,
        phone: phoneclient,
        email: emailclient,
        marque: marque,
        civilite: civiliteclient,
      },
      project_address: {
        address: adresseprojet,
        postal_code: postalprojet,
        city: villeprojet,
      },
      invoice: {
        num_devis: numdevis,
        date_emission: date,
        date_validite: datevalidite,
        tva: "20%",
        base_ht_string: base_ht_string,
        supplement_string: supplement_string,
        total_remise_string: total_remise_string,
        total_ht_string: total_ht_string,
        tva_string: tva_string,
        total_ttc_string: total_ttc_string,
        nbr_maisons: nbr_maisons,
      },
      building_infos: {
        surface: surface,
        nbr_maisons: nbr_maisons,
      },
      bank: { name: banque, iban: iban, bic: bic },
      payment_conditions: conditionsreglement,
      cancellation_policy: politiqueannulation,
      conditions_generales_vente: conditions_generales_vente,
      complementary_infos: devis.complementary_infos || "",
      failure_policy: politiqueechec,
      products: theproducts,
      author: author,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    // Perform the POST request using fetch
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(data),
    });

    const auth = getAuthCentral();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return "User not logged in";
    }

    const db = getFirestore();
    const userRef = doc(db, "user", currentUser.uid);
    const userSnapshot = await getDoc(checkDB(userRef));
    const responseText = await response.text();

    if (userSnapshot.exists()) {
      const org_id = userSnapshot.get("org_id");
      await addLogDocument(
        url,
        prefix,
        userRef,
        org_id,
        response.status?.toString(),
        "", // Additional fields (optional)
        "",
        JSON.stringify(data),
        responseText,
        "POST"
      );
    }

    // Log the response using addLogDocument

    // Check if the request was successful
    if (response.ok) {
      const responseData = JSON.parse(responseText);
      return responseData;
    } else {
      console.error(`Failed to create invoice: ${response.status}`);
      return "";
    }
  } catch (error) {
    console.error(`Exception occurred: ${error.message}`);
    return "";
  }
};

// Helper function to format currency
const formatmoney = (value) => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
};

export default createNewDevis;
